'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useMediaSource,
  type MediaItem,
  type MediaVideoDetail,
} from '@akademi/site-builder-runtime';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { SectionShell } from '../_shared/SectionShell';
import { SkeletonRow } from '../_shared/SkeletonRow';
import { EmptyState } from '../_shared/EmptyState';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { StaleIndicator } from '../_shared/StaleIndicator';
import { ContentRail } from '../ui/ContentRail';
import { prettify } from '../_shared/prettify';
import { MediaDetailModal } from './MediaDetailModal';

/**
 * Horizontal scroll-snap row of poster cards. Backed by
 * {@code MediaSource.videos.fetchSource(filter)}. Tap a card → opens
 * {@link MediaDetailModal} with the item's full metadata + a CTA.
 *
 * <p>{@code cardAspect} picks the poster ratio:
 * {@code poster} (2:3, default), {@code landscape} (16:9), or
 * {@code square}.
 */
export type MediaCardAspect = 'poster' | 'landscape' | 'square';

export interface MediaCardRowProps {
  title?: string;
  filter?: string;
  kicker?: string;
  ctaLabel?: string;
  cardAspect?: MediaCardAspect;
  limit?: number;
  videoHrefTemplate?: string;
}

function aspect(a: MediaCardAspect): string {
  if (a === 'landscape') return '16 / 9';
  if (a === 'square') return '1 / 1';
  return '2 / 3';
}

export function MediaCardRow({
  title,
  filter: rawFilter,
  kicker,
  ctaLabel: authoredCtaLabel,
  cardAspect = 'poster',
  limit = 12,
  videoHrefTemplate = '/tube/{slug}',
}: MediaCardRowProps) {
  const { t } = useTranslation('site-builder-blocks');
  const ctaLabel = authoredCtaLabel ?? t('mediaCardRow.cta');
  const media = useMediaSource();
  const filter = (rawFilter ?? '').trim() || 'trending';
  const [openSlug, setOpenSlug] = useState<string | null>(null);

  const state = useAsyncResource<{ items: MediaItem[]; label?: string }>(
    async (signal) => {
      if (!media) return null;
      const res = await media.videos.fetchSource({ source: filter, limit, signal });
      return res && res.items.length > 0 ? res : null;
    },
    [media, filter, limit],
  );

  const detailState = useAsyncResource<MediaVideoDetail>(
    async (signal) => {
      if (!media || !openSlug) return null;
      return await media.videos.fetchBySlug(openSlug, signal);
    },
    [media, openSlug],
  );

  if (!media) {
    return (
      <SectionShell title={title || prettify(filter)}>
        <AdapterPlaceholder blockType="MediaCardRow" adapterName="MediaSource" />
      </SectionShell>
    );
  }
  const headerTitle =
    title || (state.kind === 'loaded' ? state.data.label : undefined) || prettify(filter);

  const titleNode = (
    <>
      {headerTitle}
      {state.kind === 'loaded' && (state.data as { stale?: boolean }).stale === true && (
        <span style={{ marginLeft: 12, display: 'inline-block', verticalAlign: 'middle' }}>
          <StaleIndicator />
        </span>
      )}
    </>
  );

  if (state.kind === 'loading') {
    return (
      <SectionShell title={titleNode}>
        {kicker && <Kicker text={kicker} />}
        <SkeletonRow count={limit} layout="rail" aspect={aspect(cardAspect)} minCardWidth={180} />
      </SectionShell>
    );
  }

  if (state.kind !== 'loaded') {
    return (
      <SectionShell title={titleNode}>
        {kicker && <Kicker text={kicker} />}
        <EmptyState
          title={t('mediaCardRow.empty.title')}
          hint={t('mediaCardRow.empty.hint')}
          diagnostic={filter}
        />
      </SectionShell>
    );
  }

  return (
    <SectionShell title={titleNode}>
      {kicker && <Kicker text={kicker} />}
      <ContentRail ariaLabel={headerTitle} itemWidth="200px" snap="start">
        {state.data.items.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setOpenSlug(item.slug)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              padding: 0,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <div
              aria-hidden
              style={{
                aspectRatio: aspect(cardAspect),
                borderRadius: 'var(--radius-md, 8px)',
                background: item.imageUrl
                  ? `url(${item.imageUrl}) center/cover no-repeat`
                  : 'var(--color-neutral-200, #e4e4e7)',
              }}
            />
            <div style={{ fontSize: '13px', fontWeight: 600, lineHeight: 1.3 }}>{item.title}</div>
          </button>
        ))}
      </ContentRail>
      {openSlug && (
        <MediaDetailModal
          open={!!openSlug}
          onClose={() => setOpenSlug(null)}
          video={detailState.kind === 'loaded' ? detailState.data : null}
          loading={detailState.kind === 'loading'}
          ctaLabel={ctaLabel}
          videoHrefTemplate={videoHrefTemplate}
        />
      )}
    </SectionShell>
  );
}

function Kicker({ text }: { text: string }) {
  return (
    <div
      style={{
        fontSize: '11px',
        fontWeight: 700,
        letterSpacing: 'var(--font-tracking-widest, 0.14em)',
        textTransform: 'uppercase',
        color: 'var(--color-text-tertiary, #94867d)',
        marginBottom: 8,
      }}
    >
      {text}
    </div>
  );
}
